import React from 'react';
import { Box, Typography, Alert } from '@mui/material';
import Editor from '@monaco-editor/react';
import { useTheme } from '@/hooks';

interface JsonEditorProps {
  value: string;
  onChange?: (value: string) => void;
  height?: string | number;
  readOnly?: boolean;
  error?: string | null;
  label?: string;
  helperText?: string;
}

const JsonEditor: React.FC<JsonEditorProps> = ({
  value,
  onChange,
  height = 350,
  readOnly = false,
  error,
  label,
  helperText,
}) => {
  const { mode } = useTheme();

  return (
    <Box sx={{ width: '100%' }}>
      {label && (
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          {label}
        </Typography>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Box
        sx={{
          border: '1px solid',
          borderRadius: 1,
          overflow: 'hidden',
          '& .monaco-editor': {
            borderRadius: 1,
          },
        }}
      >
        <Editor
          height={height}
          defaultLanguage="json"
          value={value}
          onChange={(newValue) => onChange?.(newValue || '')}
          loading={<div>Loading JSON Editor...</div>}
          options={{
            readOnly,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            lineNumbers: 'on',
            folding: true,
            foldingStrategy: 'indentation',
            showFoldingControls: 'always',
            selectOnLineNumbers: true,
            automaticLayout: true,
            fontSize: 12,
            formatOnPaste: true,
            formatOnType: true,
            tabSize: 2,
            insertSpaces: true,
          }}
          theme={mode === 'dark' ? 'vs-dark' : 'vs'}
        />
      </Box>
      
      {helperText && !error && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default JsonEditor;